import { fromJS } from 'immutable';
import { handleActions } from 'redux-actions';

import {
  CHARTIQ_ADD_COMPARISON,
  CHARTIQ_CHANGE_CHART_DATA,
  CHARTIQ_CHANGE_VECTOR_LINE_PARAMS,
  CHARTIQ_CHANGE_VECTOR_PARAMS,
  CHARTIQ_CHANGE_VECTOR_STYLE,
  CHARTIQ_CLEAR,
  CHARTIQ_DRAW,
  CHARTIQ_DRAWINGS_CHANGED,
  CHARTIQ_IMPORT_DRAWINGS,
  // CHARTIQ_IMPORT_LAYOUT,
  // CHARTIQ_SHARE_CHART,
  CHARTIQ_LAYOUT_CHANGED,
  CHARTIQ_REDID,
  CHARTIQ_REMOVE_COMPARISON,
  CHARTIQ_SAVE_LAYOUT,
  // CHARTIQ_TOGGLE_AXIS_LABELS,
  CHARTIQ_SET_CHART_TYPE,
  CHARTIQ_SET_PERIODICITY,
  CHARTIQ_SET_RESPONSIVE_SIZE,
  CHARTIQ_SET_SHARE_STATUS,
  // CHARTIQ_CREATE_UNDO_STAMP,
  // CHARTIQ_UPDATE_UNDO_STAMPS,
  CHARTIQ_SET_SYMBOL,
  CHARTIQ_SUBSCRIBE_CALLBACK,
  CHARTIQ_TOGGLE_CROSSHAIRS,
  CHARTIQ_TOGGLE_TIMEZONE_MODAL,
  CHARTIQ_UNDID,
  CHARTIQ_UNDO_STAMPS,
 } from './constants';

import { IOldOhlcResponse } from 'components/ChartIQ/Quotefeed';
import {
  SET_CHART_DATA,
  SET_QUOTES_DATA,
  SET_VPMES_DATA,
  SET_VPMNDX_DATA,
  SET_VPMNQ_DATA,
  SET_VPMSPX_DATA,
} from '../../socket/constants';

const initialState = fromJS({
  canClear: false,
  canRedo: false,
  canUndo: false,
  chartSeries: [],
  chartTop: 0,
  chartType: null,
  ciq: null,
  coeff: 0,
  comparisons: [],
  drawings: [],
  initialTool: undefined,
  periodicity: {
    interval: 1,
    period: 1,
    timeUnit: 'day',
  },
  redoStack: [],
  refreshInterval: 1,
  responsiveSize: 'break-sm',
  shareStatus: 'HIDDEN',
  shareStatusMsg: null,
  showAxisLabels: true,
  showCrosshairs: false,
  showDrawingToolbar: false,
  showPeriodicityLoader: false,
  showTimezoneModal: false,
  streamCallback: null,
  studyOverlay: {
    left: 0,
    params: null,
    show: false,
    top: 0,
  },
  symbol: 'VPMES1',
  undoStack: [],
});

const CIQ = (window as any).CIQ;

interface IStreamingData {
  name: string
  data: IOldOhlcResponse & { v: number }
}

function processStreamingData(state: any, payload: IStreamingData) {
  const scb = state.get('streamCallback');
  if (scb && scb.symbol === payload.name) {
    const data = payload.data;
    // const coeff = state.get('coeff');
    // const date = new Date(Math.floor(new Date(data.date).getTime() / coeff) * coeff);

    const updatedDate = [
      {
        Close: data.close,
        Date: data.date,
        High: data.high,
        Low: data.low,
        Open: data.open,
        Percentile10: parseFloat(data.percentile10),
        Percentile90: parseFloat(data.percentile90),
        Rvol: data.rvol,
        Rvolmin: data.rvolmin,
        Tftf10_open: parseFloat(data.tftf10_open),
        Tftf90_open: parseFloat(data.tftf90_open),
        Vix: data.vix,
        Volume: data.v
      },
    ];
    scb.ciq.updateChartData(updatedDate);
  }
  return state;
}

export default handleActions({
  [CHARTIQ_CHANGE_CHART_DATA]: (state: any, { payload }) => state.set('isLoadingPeriodicity', payload),
  [CHARTIQ_SET_CHART_TYPE]: (state: any, { payload }) => state.set('chartType', payload.chartType),
  [CHARTIQ_SET_RESPONSIVE_SIZE]: (state: any, { payload }) => state.set('responsiveSize', payload),
  [CHARTIQ_SET_PERIODICITY]: (state: any, { payload }) => {
    let coeff = 0;
    switch (payload.periodicity.timeUnit) {
      case 'second':
          coeff = 1000 * payload.periodicity.interval;
        break;
      case 'minute':
          coeff = 1000 * 60 * payload.periodicity.interval;
        break;
      case 'hour':
          coeff = 1000 * 60 * 60 * payload.periodicity.interval;
        break;
      case 'day':
          coeff = 1000 * 60 * 60 * 24 * payload.periodicity.interval;
        break;
      default:
        break;
    }
    return state.set('periodicity', fromJS(payload.periodicity)).set('coeff', coeff);
  },
  [CHARTIQ_TOGGLE_CROSSHAIRS]: (state, data) => {
    const { ciq } = data.payload;
    const newState = !ciq.layout.crosshair;
    ciq.layout.crosshair = newState;
    return state.set('showCrosshairs', newState);
  },
  [CHARTIQ_IMPORT_DRAWINGS]: (state: any, { payload }) => {
    const { ciq } = payload;
    const drawings = ciq.drawingsObjects.slice();
    return state.set('drawings', drawings).set('canClear', drawings.length > 0);
  },
  [CHARTIQ_DRAW]: (state, action) => {
    const { ciq } = action.payload;
    ciq.draw();
    const rawState = state.toJS();
    return state
    .set('canUndo', rawState.undoStack.length > 0)
    .set('canRedo', rawState.redoStack.length > 0)
    .set('canClear', ciq.drawingObjects.length > 0);
  },
  [CHARTIQ_CHANGE_VECTOR_PARAMS]: (state: any, { payload }) => {
    let retState = state;
    const { ciq, tool } = payload;
    const style = ciq.canvasStyle('stx_annotation');
    if (style) {
      ciq.currentVectorParameters.annotation.font.size = style.size;
      ciq.currentVectorParameters.annotation.font.family = style.family;
      ciq.currentVectorParameters.annotation.font.style = style.style;
      ciq.currentVectorParameters.annotation.font.weight = style.weight;
    }
    const container = document.getElementById('chartContainer')
    if (container) {
      const toolbarStatus = container.classList.contains('toolbarOn');
      const newTool = (!tool && toolbarStatus && retState.get('initialTool')) ? retState.get('initialTool') : tool;
      ciq.changeVectorType(newTool);
    }
    if (!state.get('initialTool')) {
      retState = retState.set('initialTool', tool);
    }
    if (tool && retState.get('initialTool') !== tool) {
      retState = retState.set('initialTool', tool);
    }
    return retState;
  },
  [CHARTIQ_LAYOUT_CHANGED]: (state: any, { payload }) => state.set('chartTop', payload.ciq.chart.top),

  [CHARTIQ_CLEAR]: state => state,
  [CHARTIQ_ADD_COMPARISON]: (state: any, { payload }) => {
    const { ciq, series } = payload;
    if (!series) { return state; }
    const seriesArray = Array.isArray(series) ? series : [series];
    const newComparisons = state.get('comparisons').toJS().concat(seriesArray);
    return state
    .set('comparisons', fromJS(newComparisons))
    .set('chartTop', ciq.chart.top);
  },
  [CHARTIQ_REMOVE_COMPARISON]: (state: any, { payload }) => {
    const newComparisons = state.get('comparisons').toJS().filter((comp: any) => comp.id !== payload.comparison);
    return state
    .set('comparisons', fromJS(newComparisons));
  },
  [CHARTIQ_SET_SHARE_STATUS]: (state: any, { payload }) => state.set('shareStatus', payload.status).set('shareStatusMsg', payload.msg),
  [CHARTIQ_CHANGE_VECTOR_STYLE]: (state: any, { payload }) => {
    const { ciq, styleType, style } = payload;
    switch (styleType) {
      case 'bold':
        ciq.currentVectorParameters.annotation.font.weight = !style.bold ? 'bold' : 'normal';
        break;
      case 'italic':
        ciq.currentVectorParameters.annotation.font.style = !style.italic ? 'italic' : 'normal';
        break;
      case 'family':
        ciq.currentVectorParameters.annotation.font.family = style.family;
        break;
      case 'size':
        ciq.currentVectorParameters.annotation.font.size = style.size + 'px';
        break;
      case 'lineColor':
        ciq.currentVectorParameters.currentColor = CIQ.hexToRgba('#' + style.color);
        break;
      case 'fillColor':
        ciq.currentVectorParameters.fillColor = CIQ.hexToRgba('#' + style.color);
        break;
      default:
        break;
    }

    return state;
  },
  [CHARTIQ_CHANGE_VECTOR_LINE_PARAMS]: (state: any, { payload }) => {
    const { ciq, weight, pattern } = payload;
    ciq.currentVectorParameters.lineWidth = weight;
    ciq.currentVectorParameters.pattern = pattern;
    return state;
  },
  [CHARTIQ_DRAWINGS_CHANGED]: (state: any, { payload }) => {
    const { ciq } = payload;
    const drawings = ciq.drawingObjects.slice();
    return state
    .set('drawings', fromJS(drawings));
  },
  [CHARTIQ_SET_SYMBOL]: (state: any, { payload }) => state.set('symbol', payload.symbol),
  [CHARTIQ_REDID]: (state: any, { payload }) => {
    const newUndoStack = state.get('undoStack').toJS().slice();
    newUndoStack.push(payload);
    return state
    .set('undoStack', fromJS(newUndoStack));
  },
  [CHARTIQ_TOGGLE_TIMEZONE_MODAL]: () => { throw new Error('not implemented'); },
  [CHARTIQ_UNDID]: (state: any, { payload }) => {
    const newRedoStack = state.get('redoStack').toJS().slice();
    newRedoStack.push(payload);
    return state
    .set('canRedo', true)
    .set('redoStack', fromJS(newRedoStack));
  },
  [CHARTIQ_UNDO_STAMPS]: (state: any, { payload }) => {
    const { params, ciq } = payload;
    const stack = state.get('undoStack').toJS().slice();
    stack.push(params.before);
    return state
    .set('undoStack', fromJS(stack))
    .set('canUndo', stack.length > 0)
    .set('canRedo', state.get('redoStack').toJS().length > 0)
    .set('canClear', ciq.drawingObjects.length > 0);
  },
  [CHARTIQ_SAVE_LAYOUT]: (state: any, { payload }) => {
    const { ciq } = payload;
    const savedLayout = JSON.stringify(ciq.exportLayout({ withSymbols: true }));
    CIQ.localStorageSetItem('myChartLayout', savedLayout);
    CIQ.localStorageSetItem('myChartPreferences', JSON.stringify(ciq.exportPreferences()));
    return state;
  },
  [CHARTIQ_SUBSCRIBE_CALLBACK]: (state: any, { payload }) => state.set('streamCallback', payload),
  // tap into data stream
  [SET_VPMES_DATA]: (state: any, { payload }) => processStreamingData(state, payload),
  [SET_VPMSPX_DATA]: (state: any, { payload }) => processStreamingData(state, payload),
  [SET_VPMNQ_DATA]: (state: any, { payload }) => processStreamingData(state, payload),
  [SET_VPMNDX_DATA]: (state: any, { payload }) => processStreamingData(state, payload),
  [SET_CHART_DATA]: (state: any, { payload }) => {
    const scb = state.get('streamCallback');
    if (scb) {
      const data = payload.data;
      scb.ciq.updateChartData([
        {
          Close: data.c,
          Date: data.time,
          High: data.h,
          Low: data.l,
          Open: data.o,
          Percentile10: parseFloat(data.percentile10),
          Percentile90: parseFloat(data.percentile90),
          Rvol: data.rvol,
          Rvolmin: data.rvolmin,
          Tftf10_open: parseFloat(data.tftf10_open),
          Tftf90_open: parseFloat(data.tftf90_open),
          Vix: data.vix,
          Volume: data.v
        },
      ]);
    }
    return state;
  },
  [SET_QUOTES_DATA]: (state: any, { payload }) => {
    const scb = state.get('streamCallback');
    if (scb && payload.name === scb.actualSymbol) {
      const data = payload.data;
      scb.ciq.updateChartData({
        Date: data.t,
        Last: data.p,
      },
      null,
      {
        fillGaps: true,
        useAsLastSale: true,
      },
      );
    }
    return state;
  },
}, initialState);
